import { createFileRoute } from "@tanstack/react-router";
import { TrendingUp, MousePointerClick, DollarSign, Target } from "lucide-react";
import { INDUSTRY_BENCHMARKS, formatRange } from "@/lib/industry-benchmarks";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useI18n } from "@/lib/i18n";

export const Route = createFileRoute("/_app/benchmarks")({
  component: BenchmarksPage,
  head: () => ({
    meta: [
      { title: "Industry Benchmarks · AdKavi" },
      { name: "description", content: "Industry CTR, CPC, CPL and ROAS benchmarks for paid social and search." },
      { name: "robots", content: "noindex" },
    ],
  }),
});

function BenchmarksPage() {
  const { t } = useI18n();

  return (
    <section className="p-6 lg:p-10 max-w-7xl mx-auto">
      <header className="mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-3">
          <TrendingUp className="w-3.5 h-3.5" /> {t("bench.badge")}
        </div>
        <h1 className="text-3xl lg:text-4xl font-bold tracking-tight flex items-center gap-3">
          <TrendingUp className="w-8 h-8 text-orange-400 shrink-0" />
          <span className="bg-gradient-to-r from-orange-400 to-primary bg-clip-text text-transparent">{t("bench.title")}</span>
        </h1>
        <p className="mt-2 text-muted-foreground max-w-2xl">
          {t("bench.desc")}
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <KpiCard icon={<MousePointerClick className="w-4 h-4" />} label="CTR" hint={t("bench.higher")} />
        <KpiCard icon={<DollarSign className="w-4 h-4" />} label="CPC" hint={t("bench.lower")} />
        <KpiCard icon={<Target className="w-4 h-4" />} label="CPL" hint={t("bench.lower")} />
        <KpiCard icon={<TrendingUp className="w-4 h-4" />} label="ROAS" hint={t("bench.higher")} />
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("bench.col.industry")}</TableHead>
              <TableHead className="text-right">{t("bench.col.ctr")}</TableHead>
              <TableHead className="text-right">{t("bench.col.cpc")}</TableHead>
              <TableHead className="text-right">{t("bench.col.cpl")}</TableHead>
              <TableHead className="text-right">{t("bench.col.roas")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {INDUSTRY_BENCHMARKS.map((b) => (
              <TableRow key={b.id}>
                <TableCell className="font-medium">
                  <span className="mr-2">{b.emoji}</span>
                  {b.label}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {b.ctrAverage.toFixed(1)}% <span className="text-muted-foreground">/</span>{" "}
                  <Badge variant="outline" className="ml-1">{b.ctrGood.toFixed(1)}%+</Badge>
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  ${b.cpcAverage.toFixed(2)} <span className="text-muted-foreground">/</span>{" "}
                  <Badge variant="outline" className="ml-1">≤${b.cpcLow.toFixed(2)}</Badge>
                </TableCell>
                <TableCell className="text-right tabular-nums">{formatRange(b.good)}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {b.roasAverage.toFixed(1)}x <span className="text-muted-foreground">/</span>{" "}
                  <Badge variant="outline" className="ml-1">{b.roasGood.toFixed(1)}x+</Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <p className="mt-4 text-xs text-muted-foreground">
        {t("bench.sources")}
      </p>
    </section>
  );
}

function KpiCard({ icon, label, hint }: { icon: React.ReactNode; label: string; hint: string }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <div className="flex items-center gap-2 text-muted-foreground text-xs">
        {icon} {hint}
      </div>
      <div className="mt-2 text-2xl font-bold">{label}</div>
    </div>
  );
}
